import { useCallback, useMemo } from 'react';
import { ApolloError } from '@apollo/client';

import { useDashboardConversationsQuery } from 'core/graphql';

const PAGE_SIZE = 20;

export interface IBrowserConversation {
  id: string;
  kind: string;
  /** Human dialog title (channel name / mail subject / counterpart name). */
  displayTitle: string;
  mailboxEmail: string | null;
  status: string | null;
  unreadCount: number;
  isUnanswered: boolean;
  lastMessageAt: string | null;
  student: { id: string; firstName: string; lastName: string } | null;
  group: { id: string; name: string } | null;
  /** Group owning the Slack workspace — set even when `group` is not. */
  workspaceGroup: { id: string; name: string } | null;
}

export interface IConversationsBrowserParams {
  search?: string;
  groupId?: string;
  studentId?: string;
  dateFrom?: string;
  dateTo?: string;
  unansweredOnly?: boolean;
  /** Keep only these conversation kinds (e.g. ['gmail_thread'] for the
   * communicator's «Почта» tab — spec 0008). */
  kinds?: string[];
  /** Re-poll the first cursor page (badge freshness safety net). */
  pollInterval?: number;
  /** Skip the query entirely (no target selected yet). */
  skip?: boolean;
}

interface IUseConversationsBrowser {
  conversations: IBrowserConversation[];
  totalCount: number;
  hasNextPage: boolean;
  /** Pull the next cursor page — appended by the relay cache policy. */
  loadMore: () => void;
  loading: boolean;
  /** Query error — lets the panel show "failed to load" instead of "empty". */
  error?: ApolloError;
}

/**
 * Dashboard communications browser (Slack workspace rework): every mirrored
 * conversation across all groups, filterable by search / group / student /
 * date range, unanswered-only by default, cursor-paginated. Appending is done
 * by the `conversationsBrowser` relayStylePagination cache policy
 * (core/cache) — no manual `updateQuery` concatenation.
 */
export const useConversationsBrowser = ({
  search,
  groupId,
  studentId,
  dateFrom,
  dateTo,
  unansweredOnly,
  kinds,
  pollInterval,
  skip,
}: IConversationsBrowserParams): IUseConversationsBrowser => {
  // errorPolicy:'all' so a PermissionDenied degrades gracefully — the panel
  // gets whatever data survived plus the error to distinguish "empty" from
  // "the query failed".
  const { data, loading, error, fetchMore } = useDashboardConversationsQuery({
    variables: {
      search: search || undefined,
      groupId: groupId || undefined,
      studentId: studentId || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      unansweredOnly,
      kinds: kinds || undefined,
      first: PAGE_SIZE,
    },
    skip,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
    pollInterval,
  });

  const connection = data?.conversationsBrowser;
  const pageInfo = connection?.pageInfo;

  const loadMore = useCallback(() => {
    if (!pageInfo?.hasNextPage) return;
    fetchMore({
      variables: { after: pageInfo.endCursor },
    }).catch(() => {});
  }, [fetchMore, pageInfo]);

  const conversations = useMemo<IBrowserConversation[]>(
    () =>
      (connection?.edges || [])
        .map((edge) => edge?.node)
        .filter((node): node is NonNullable<typeof node> => Boolean(node))
        .map((node) => ({
          id: node.id,
          kind: node.kind || '',
          displayTitle: node.displayTitle || '',
          mailboxEmail: node.mailboxEmail || null,
          status: node.status || null,
          unreadCount: node.unreadCount || 0,
          isUnanswered: Boolean(node.isUnanswered),
          lastMessageAt: node.lastMessageAt || null,
          student: node.student
            ? {
                id: node.student.id,
                firstName: node.student.user?.firstName || '',
                lastName: node.student.user?.lastName || '',
              }
            : null,
          group: node.group
            ? { id: node.group.id, name: node.group.name }
            : null,
          workspaceGroup: node.workspaceGroup
            ? { id: node.workspaceGroup.id, name: node.workspaceGroup.name }
            : null,
        })),
    [connection],
  );

  return {
    conversations,
    totalCount: connection?.totalCount || 0,
    hasNextPage: Boolean(pageInfo?.hasNextPage),
    loadMore,
    loading,
    error,
  };
};
